import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SlideOver } from '@/components/SlideOver';
import { getPublicAssets, updatePublicAssets, uploadCourseMedia, type ConnectSocialLink, type PublicAssets } from '@/lib/api';

const FOOTER_LOGO_RECOMMENDED = '200×60';
const DEFAULT_SOCIAL_IDS = ['twitter', 'linkedin', 'facebook', 'instagram'] as const;
const DEFAULT_SOCIAL_LABELS: Record<string, string> = {
  twitter: 'Twitter / X',
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  instagram: 'Instagram',
};
const DEFAULT_ABOUT_HERO_IMAGES = [
  'https://images.pexels.com/photos/2417842/pexels-photo-2417842.jpeg?auto=compress&cs=tinysrgb&w=2200',
  'https://images.pexels.com/photos/290595/pexels-photo-290595.jpeg?auto=compress&cs=tinysrgb&w=2200',
  'https://images.pexels.com/photos/34098/south-africa-hluhluwe-imfolozi-park-landscape-scenery.jpg?auto=compress&cs=tinysrgb&w=2200',
  'https://images.pexels.com/photos/247431/pexels-photo-247431.jpeg?auto=compress&cs=tinysrgb&w=2200',
  'https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&w=2200',
  'https://images.pexels.com/photos/1181396/pexels-photo-1181396.jpeg?auto=compress&cs=tinysrgb&w=2200',
] as const;
const DEFAULT_ABOUT_VALUE_PILLARS = [
  {
    title: 'Venture programs',
    body: 'Structured, execution-first programs that take founders from idea to traction. We combine rigorous curriculum with direct operator support so teams do not just learn, they ship.',
    image: 'https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&w=1400',
  },
  {
    title: 'Talent and learning',
    body: 'We develop operators, not just founders. Homeland trains product thinkers, engineers, and business leaders who can build and scale across sectors.',
    image: 'https://images.pexels.com/photos/1181690/pexels-photo-1181690.jpeg?auto=compress&cs=tinysrgb&w=1400',
  },
  {
    title: 'Capital and partnerships',
    body: 'We unlock capital through a curated network of investors, corporates, and ecosystem partners. From pilots to distribution, we help ventures move faster than they could alone.',
    image: 'https://images.pexels.com/photos/7654580/pexels-photo-7654580.jpeg?auto=compress&cs=tinysrgb&w=1400',
  },
] as const;
const DEFAULT_EAST_AFRICA_CITY_IMAGES = [
  {
    city: 'Nairobi, Kenya',
    image:
      'https://images.pexels.com/photos/2417842/pexels-photo-2417842.jpeg?auto=compress&cs=tinysrgb&w=1400',
  },
  {
    city: 'Kampala, Uganda',
    image:
      'https://images.pexels.com/photos/290595/pexels-photo-290595.jpeg?auto=compress&cs=tinysrgb&w=1400',
  },
  {
    city: 'Kigali, Rwanda',
    image:
      'https://images.pexels.com/photos/1181408/pexels-photo-1181408.jpeg?auto=compress&cs=tinysrgb&w=1400',
  },
  {
    city: 'Dar es Salaam, Tanzania',
    image:
      'https://images.pexels.com/photos/1181675/pexels-photo-1181675.jpeg?auto=compress&cs=tinysrgb&w=1400',
  },
] as const;

interface PublicAssetsSlideOverProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

function mergeSocialLinks(saved: ConnectSocialLink[]): ConnectSocialLink[] {
  const byId = new Map<string, ConnectSocialLink>();
  for (const id of DEFAULT_SOCIAL_IDS) {
    byId.set(id, { id, label: DEFAULT_SOCIAL_LABELS[id] ?? id, url: '' });
  }
  for (const s of saved) {
    byId.set(s.id, {
      id: s.id,
      label: s.label || (DEFAULT_SOCIAL_LABELS[s.id] ?? s.id),
      url: s.url || '',
    });
  }
  return Array.from(byId.values());
}

export function PublicAssetsSlideOver({ open, onClose, onSaved }: PublicAssetsSlideOverProps) {
  const [heroVideoUrl, setHeroVideoUrl] = useState('');
  const [headerLogoUrl, setHeaderLogoUrl] = useState('');
  const [headerLogoWidth, setHeaderLogoWidth] = useState('');
  const [headerLogoHeight, setHeaderLogoHeight] = useState('');
  const [headerLogoAlign, setHeaderLogoAlign] = useState<'left' | 'center'>('left');
  const [footerLogoUrl, setFooterLogoUrl] = useState('');
  const [footerLogoWidth, setFooterLogoWidth] = useState('');
  const [footerLogoHeight, setFooterLogoHeight] = useState('');
  const [footerLogoAlign, setFooterLogoAlign] = useState<'left' | 'center'>('left');
  const [faviconUrl, setFaviconUrl] = useState('');
  const [faviconWidth, setFaviconWidth] = useState('32');
  const [faviconHeight, setFaviconHeight] = useState('32');
  const [connectEmail, setConnectEmail] = useState('');
  const [connectPhone, setConnectPhone] = useState('');
  const [connectAddress, setConnectAddress] = useState('');
  const [connectEmailNairobi, setConnectEmailNairobi] = useState('');
  const [connectPhoneNairobi, setConnectPhoneNairobi] = useState('');
  const [connectAddressNairobi, setConnectAddressNairobi] = useState('');
  const [connectWhatsappUrl, setConnectWhatsappUrl] = useState('');
  const [connectSocialLinks, setConnectSocialLinks] = useState<ConnectSocialLink[]>(() =>
    DEFAULT_SOCIAL_IDS.map((id) => ({ id, label: DEFAULT_SOCIAL_LABELS[id], url: '' }))
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [headerLogoUploading, setHeaderLogoUploading] = useState(false);
  const [footerLogoUploading, setFooterLogoUploading] = useState(false);
  const [faviconUploading, setFaviconUploading] = useState(false);
  const headerLogoFileRef = useRef<HTMLInputElement>(null);
  const logoFileRef = useRef<HTMLInputElement>(null);
  const faviconFileRef = useRef<HTMLInputElement>(null);

  // Extended marketing / ecosystem config
  const [featuredPrograms, setFeaturedPrograms] = useState<any[]>([]);
  const [ecosystemStats, setEcosystemStats] = useState<any[]>([]);
  const [aboutMission, setAboutMission] = useState('');
  const [aboutVision, setAboutVision] = useState('');
  const [aboutMissionImage, setAboutMissionImage] = useState('');
  const [aboutBuildImage, setAboutBuildImage] = useState('');
  const [aboutWhoImage, setAboutWhoImage] = useState('');
  const [aboutHeroHeading, setAboutHeroHeading] = useState('');
  const [aboutHeroSubtext, setAboutHeroSubtext] = useState('');
  const [aboutHeroParagraph, setAboutHeroParagraph] = useState('');
  const [aboutHeroImages, setAboutHeroImages] = useState<any[]>([]);
  const [aboutLogoCarousel, setAboutLogoCarousel] = useState<{
    title: string;
    items: { url: string; name: string }[];
  }>({ title: '', items: [] });
  const [aboutValuePillars, setAboutValuePillars] = useState<any[]>([]);
  const [aboutCoreValues, setAboutCoreValues] = useState<any[]>([]);
  const [aboutBuildAreas, setAboutBuildAreas] = useState<any[]>([]);
  const [aboutWhoFor, setAboutWhoFor] = useState<any[]>([]);
  const [aboutTractionStats, setAboutTractionStats] = useState<any[]>([]);
  const [aboutEastAfricaCityImages, setAboutEastAfricaCityImages] = useState<
    Array<{ city: string; image: string }>
  >([]);
  const [ecosystemHubs, setEcosystemHubs] = useState<any[]>([]);
  const [ecosystemPartners, setEcosystemPartners] = useState<any[]>([]);
  const [ecosystemEvents, setEcosystemEvents] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'branding' | 'home' | 'about' | 'ecosystem'>(
    'branding'
  );

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getPublicAssets()
      .then((a: PublicAssets) => {
        setHeroVideoUrl(a.heroVideoUrl ?? '');
        setHeaderLogoUrl(a.headerLogoUrl ?? '');
        setHeaderLogoWidth(a.headerLogoWidth ?? '');
        setHeaderLogoHeight(a.headerLogoHeight ?? '');
        setHeaderLogoAlign((a.headerLogoAlign === 'center' ? 'center' : 'left'));
        setFooterLogoUrl(a.footerLogoUrl ?? '');
        setFooterLogoWidth(a.footerLogoWidth ?? '');
        setFooterLogoHeight(a.footerLogoHeight ?? '');
        setFooterLogoAlign((a.footerLogoAlign === 'center' ? 'center' : 'left'));
        setFaviconUrl(a.faviconUrl ?? '');
        setFaviconWidth(a.faviconWidth ?? '32');
        setFaviconHeight(a.faviconHeight ?? '32');
        setConnectEmail(a.connectEmail ?? '');
        setConnectPhone(a.connectPhone ?? '');
        setConnectAddress(a.connectAddress ?? '');
        setConnectEmailNairobi(a.connectEmailNairobi ?? '');
        setConnectPhoneNairobi(a.connectPhoneNairobi ?? '');
        setConnectAddressNairobi(a.connectAddressNairobi ?? '');
        setConnectWhatsappUrl(a.connectWhatsappUrl ?? '');
        setConnectSocialLinks(mergeSocialLinks(a.connectSocialLinks ?? []));
        setFeaturedPrograms(a.featuredPrograms ?? []);
        setEcosystemStats(a.ecosystemStats ?? []);
        setAboutMission(a.aboutMission ?? '');
        setAboutVision(a.aboutVision ?? '');
        setAboutMissionImage(a.aboutMissionImage ?? '');
        setAboutBuildImage(a.aboutBuildImage ?? '');
        setAboutWhoImage(a.aboutWhoImage ?? '');
        setAboutHeroHeading(a.aboutHeroHeading ?? '');
        setAboutHeroSubtext(a.aboutHeroSubtext ?? '');
        setAboutHeroParagraph(a.aboutHeroParagraph ?? '');
        setAboutHeroImages(
          Array.isArray(a.aboutHeroImages) && a.aboutHeroImages.filter((x) => String(x ?? '').trim()).length > 0
            ? a.aboutHeroImages
            : [...DEFAULT_ABOUT_HERO_IMAGES]
        );
        {
          const lc = a.aboutLogoCarousel;
          if (lc && typeof lc === 'object' && !Array.isArray(lc) && Array.isArray(lc.items)) {
            setAboutLogoCarousel({
              title: typeof lc.title === 'string' ? lc.title : '',
              items: lc.items.map((it: { url?: string; name?: string }) => ({
                url: typeof it?.url === 'string' ? it.url : '',
                name: typeof it?.name === 'string' ? it.name : '',
              })),
            });
          } else if (Array.isArray(lc)) {
            setAboutLogoCarousel({
              title: '',
              items: lc.map((x: unknown) =>
                typeof x === 'string'
                  ? { url: x, name: '' }
                  : {
                      url: x && typeof x === 'object' && 'url' in x && typeof (x as { url: string }).url === 'string' ? (x as { url: string }).url : '',
                      name: x && typeof x === 'object' && 'name' in x && typeof (x as { name: string }).name === 'string' ? (x as { name: string }).name : '',
                    }
              ),
            });
          } else {
            setAboutLogoCarousel({ title: '', items: [] });
          }
        }
        setAboutValuePillars(
          Array.isArray(a.aboutValuePillars) && a.aboutValuePillars.length > 0
            ? a.aboutValuePillars
            : [...DEFAULT_ABOUT_VALUE_PILLARS]
        );
        setAboutCoreValues(a.aboutCoreValues ?? []);
        setAboutBuildAreas(a.aboutBuildAreas ?? []);
        setAboutWhoFor(a.aboutWhoFor ?? []);
        setAboutTractionStats(a.aboutTractionStats ?? []);
        setAboutEastAfricaCityImages(
          Array.isArray(a.aboutEastAfricaCityImages) &&
            a.aboutEastAfricaCityImages.filter((x) => String(x?.image ?? '').trim()).length > 0
            ? a.aboutEastAfricaCityImages.map((x: { city?: string; image?: string }) => ({
                city: x?.city ?? '',
                image: x?.image ?? '',
              }))
            : [...DEFAULT_EAST_AFRICA_CITY_IMAGES]
        );
        setEcosystemHubs(a.ecosystemHubs ?? []);
        setEcosystemPartners(a.ecosystemPartners ?? []);
        setEcosystemEvents(a.ecosystemEvents ?? []);
      })
      .catch(() => toast.error('Failed to load public assets'))
      .finally(() => setLoading(false));
  }, [open]);

  const setSocialUrl = (id: string, url: string) => {
    setConnectSocialLinks((prev) =>
      prev.map((s) => (s.id === id ? { ...s, url } : s))
    );
  };
  const setSocialLabel = (id: string, label: string) => {
    setConnectSocialLinks((prev) =>
      prev.map((s) => (s.id === id ? { ...s, label } : s))
    );
  };
  const addSocialLink = () => {
    const id = `custom-${Date.now()}`;
    setConnectSocialLinks((prev) => [...prev, { id, label: 'Custom', url: '' }]);
  };
  const removeSocialLink = (id: string) => {
    if (DEFAULT_SOCIAL_IDS.includes(id as (typeof DEFAULT_SOCIAL_IDS)[number])) {
      setSocialUrl(id, '');
      return;
    }
    setConnectSocialLinks((prev) => prev.filter((s) => s.id !== id));
  };

  const handleHeaderLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setHeaderLogoUploading(true);
    try {
      const { url } = await uploadCourseMedia(file);
      setHeaderLogoUrl(url);
      toast.success('Header logo uploaded. Use recommended dimensions for best appearance.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setHeaderLogoUploading(false);
      e.target.value = '';
    }
  };

  const handleFooterLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFooterLogoUploading(true);
    try {
      const { url } = await uploadCourseMedia(file);
      setFooterLogoUrl(url);
      toast.success('Footer logo uploaded. Use recommended dimensions for best appearance.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setFooterLogoUploading(false);
      e.target.value = '';
    }
  };

  const handleFaviconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFaviconUploading(true);
    try {
      const { url } = await uploadCourseMedia(file);
      setFaviconUrl(url);
      setFaviconWidth('32');
      setFaviconHeight('32');
      toast.success('Favicon uploaded. Standard size 32×32 px is set.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setFaviconUploading(false);
      e.target.value = '';
    }
  };

  const pickAndUploadImage = async (onUrl: (url: string) => void) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const { url } = await uploadCourseMedia(file);
        onUrl(url);
        toast.success('Image uploaded.');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Upload failed');
      }
    };
    input.click();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePublicAssets({
        heroVideoUrl: heroVideoUrl.trim(),
        headerLogoUrl: headerLogoUrl.trim(),
        headerLogoWidth: headerLogoWidth.trim(),
        headerLogoHeight: headerLogoHeight.trim(),
        headerLogoAlign,
        footerLogoUrl: footerLogoUrl.trim(),
        footerLogoWidth: footerLogoWidth.trim(),
        footerLogoHeight: footerLogoHeight.trim(),
        footerLogoAlign,
        faviconUrl: faviconUrl.trim(),
        faviconWidth: faviconWidth.trim(),
        faviconHeight: faviconHeight.trim(),
        connectEmail: connectEmail.trim(),
        connectPhone: connectPhone.trim(),
        connectAddress: connectAddress.trim(),
        connectEmailNairobi: connectEmailNairobi.trim(),
        connectPhoneNairobi: connectPhoneNairobi.trim(),
        connectAddressNairobi: connectAddressNairobi.trim(),
        connectWhatsappUrl: connectWhatsappUrl.trim(),
        connectSocialLinks: connectSocialLinks.filter((s) => s.url.trim()),
        featuredPrograms,
        ecosystemStats,
        aboutMission: aboutMission.trim(),
        aboutVision: aboutVision.trim(),
        aboutMissionImage: aboutMissionImage.trim(),
        aboutBuildImage: aboutBuildImage.trim(),
        aboutWhoImage: aboutWhoImage.trim(),
        aboutHeroHeading: aboutHeroHeading.trim(),
        aboutHeroSubtext: aboutHeroSubtext.trim(),
        aboutHeroParagraph: aboutHeroParagraph.trim(),
        aboutHeroImages,
        aboutLogoCarousel: {
          title: aboutLogoCarousel.title.trim(),
          items: aboutLogoCarousel.items
            .filter((it) => it.url.trim())
            .map((it) => ({
              url: it.url.trim(),
              ...(it.name.trim() ? { name: it.name.trim() } : {}),
            })),
        },
        aboutValuePillars,
        aboutCoreValues,
        aboutBuildAreas,
        aboutWhoFor,
        aboutTractionStats,
        aboutEastAfricaCityImages: aboutEastAfricaCityImages
          .filter((item) => item.image.trim())
          .map((item) => ({ city: item.city.trim(), image: item.image.trim() })),
        ecosystemHubs,
        ecosystemPartners,
        ecosystemEvents,
      });
      toast.success('Public assets saved.');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlideOver open={open} onClose={onClose} title="Edit public site content">
      <div className="space-y-8">
        <p className="text-sm text-muted-foreground">
          Edit assets and high-level content shown on public pages (home, about, ecosystem, header,
          and footer). Course card images are edited when editing each course.
        </p>
        <div className="inline-flex rounded-full border border-border bg-muted/40 p-0.5 text-xs">
          <button
            type="button"
            onClick={() => setActiveTab('branding')}
            className={`px-3 py-1.5 rounded-full ${
              activeTab === 'branding'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Branding
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('home')}
            className={`px-3 py-1.5 rounded-full ${
              activeTab === 'home'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Home
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('about')}
            className={`px-3 py-1.5 rounded-full ${
              activeTab === 'about'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            About
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('ecosystem')}
            className={`px-3 py-1.5 rounded-full ${
              activeTab === 'ecosystem'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Ecosystem
          </button>
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            {activeTab === 'branding' && (
              <>
                <div>
                  <Label className="mb-1">Home hero video URL</Label>
                  <Input
                    value={heroVideoUrl}
                    onChange={(e) => setHeroVideoUrl(e.target.value)}
                    placeholder="https://www.youtube.com/embed/… or direct video URL"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Use a YouTube embed URL or a direct video URL.
                  </p>
                </div>

                <div>
                  <Label className="mb-1">Header logo</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Recommended dimensions: <strong>180×40 px</strong> (width × height) so the logo
                    fits nicely in the top bar.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <Input
                      value={headerLogoUrl}
                      onChange={(e) => setHeaderLogoUrl(e.target.value)}
                      placeholder="Image URL or upload below"
                      className="flex-1 min-w-[200px]"
                    />
                    <input
                      ref={headerLogoFileRef}
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={handleHeaderLogoUpload}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={headerLogoUploading}
                      onClick={() => headerLogoFileRef.current?.click()}
                    >
                      {headerLogoUploading ? 'Uploading…' : 'Upload image'}
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Width (px)</Label>
                      <Input
                        type="number"
                        min={1}
                        max={600}
                        placeholder="180"
                        value={headerLogoWidth}
                        onChange={(e) =>
                          setHeaderLogoWidth(e.target.value.replace(/\D/g, '').slice(0, 4))
                        }
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Height (px)</Label>
                      <Input
                        type="number"
                        min={1}
                        max={200}
                        placeholder="40"
                        value={headerLogoHeight}
                        onChange={(e) =>
                          setHeaderLogoHeight(e.target.value.replace(/\D/g, '').slice(0, 4))
                        }
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Position</Label>
                      <select
                        value={headerLogoAlign}
                        onChange={(e) =>
                          setHeaderLogoAlign(e.target.value as 'left' | 'center')
                        }
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="mb-1">Footer logo</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Recommended dimensions: <strong>{FOOTER_LOGO_RECOMMENDED} px</strong> (width ×
                    height) so the logo looks as intended.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <Input
                      value={footerLogoUrl}
                      onChange={(e) => setFooterLogoUrl(e.target.value)}
                      placeholder="Image URL or upload below"
                      className="flex-1 min-w-[200px]"
                    />
                    <input
                      ref={logoFileRef}
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={handleFooterLogoUpload}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={footerLogoUploading}
                      onClick={() => logoFileRef.current?.click()}
                    >
                      {footerLogoUploading ? 'Uploading…' : 'Upload image'}
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Width (px)</Label>
                      <Input
                        type="number"
                        min={1}
                        max={600}
                        placeholder="200"
                        value={footerLogoWidth}
                        onChange={(e) =>
                          setFooterLogoWidth(e.target.value.replace(/\D/g, '').slice(0, 4))
                        }
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Height (px)</Label>
                      <Input
                        type="number"
                        min={1}
                        max={200}
                        placeholder="60"
                        value={footerLogoHeight}
                        onChange={(e) =>
                          setFooterLogoHeight(e.target.value.replace(/\D/g, '').slice(0, 4))
                        }
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Position</Label>
                      <select
                        value={footerLogoAlign}
                        onChange={(e) =>
                          setFooterLogoAlign(e.target.value as 'left' | 'center')
                        }
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="mb-1">Favicon</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Shown in browser tab. Recommended: square image, e.g. <strong>32×32 px</strong> or
                    16×16 for small icons.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <Input
                      value={faviconUrl}
                      onChange={(e) => setFaviconUrl(e.target.value)}
                      placeholder="Favicon URL or upload below"
                      className="flex-1 min-w-[200px]"
                    />
                    <input
                      ref={faviconFileRef}
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={handleFaviconUpload}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={faviconUploading}
                      onClick={() => faviconFileRef.current?.click()}
                    >
                      {faviconUploading ? 'Uploading…' : 'Upload image'}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Width (px)</Label>
                      <Input
                        type="number"
                        min={16}
                        max={64}
                        placeholder="32"
                        value={faviconWidth}
                        onChange={(e) =>
                          setFaviconWidth(e.target.value.replace(/\D/g, '').slice(0, 3))
                        }
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Height (px)</Label>
                      <Input
                        type="number"
                        min={16}
                        max={64}
                        placeholder="32"
                        value={faviconHeight}
                        onChange={(e) =>
                          setFaviconHeight(e.target.value.replace(/\D/g, '').slice(0, 3))
                        }
                        className="h-9"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Connect section (footer and contact page)</h4>
                  <div className="space-y-6">
                    <div>
                      <p className="text-sm font-medium text-foreground mb-3">Eldoret</p>
                      <div className="space-y-3">
                        <div>
                          <Label className="mb-1">Email</Label>
                          <Input
                            value={connectEmail}
                            onChange={(e) => setConnectEmail(e.target.value)}
                            placeholder="hello@example.org"
                            type="email"
                          />
                        </div>
                        <div>
                          <Label className="mb-1">Phone</Label>
                          <Input
                            value={connectPhone}
                            onChange={(e) => setConnectPhone(e.target.value)}
                            placeholder="+254 140 400 054"
                          />
                        </div>
                        <div>
                          <Label className="mb-1">Address (optional)</Label>
                          <Input
                            value={connectAddress}
                            onChange={(e) => setConnectAddress(e.target.value)}
                            placeholder="Zip 30100, Eldoret, Kenya (EAC)"
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground mb-3">Nairobi</p>
                      <div className="space-y-3">
                        <div>
                          <Label className="mb-1">Email</Label>
                          <Input
                            value={connectEmailNairobi}
                            onChange={(e) => setConnectEmailNairobi(e.target.value)}
                            placeholder="nairobi@example.org"
                            type="email"
                          />
                        </div>
                        <div>
                          <Label className="mb-1">Phone</Label>
                          <Input
                            value={connectPhoneNairobi}
                            onChange={(e) => setConnectPhoneNairobi(e.target.value)}
                            placeholder="+254 …"
                          />
                        </div>
                        <div>
                          <Label className="mb-1">Address (optional)</Label>
                          <Input
                            value={connectAddressNairobi}
                            onChange={(e) => setConnectAddressNairobi(e.target.value)}
                            placeholder="Nairobi, Kenya"
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label className="mb-1">WhatsApp chat link (floating button)</Label>
                      <Input
                        value={connectWhatsappUrl}
                        onChange={(e) => setConnectWhatsappUrl(e.target.value)}
                        placeholder="https://wa.me/254712345678"
                        type="url"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        When set, a green &quot;Chat on WhatsApp&quot; button appears above the footer. Leave empty to hide it.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3 mt-6">
                    <div>
                      <Label className="mb-2 block">Social media URLs</Label>
                      <div className="space-y-2">
                        {connectSocialLinks.map((s) => (
                          <div key={s.id} className="flex gap-2 items-center">
                            <Input
                              value={s.label}
                              onChange={(e) => setSocialLabel(s.id, e.target.value)}
                              placeholder="Label"
                              className="w-28 shrink-0"
                              disabled={DEFAULT_SOCIAL_IDS.includes(
                                s.id as (typeof DEFAULT_SOCIAL_IDS)[number]
                              )}
                            />
                            <Input
                              value={s.url}
                              onChange={(e) => setSocialUrl(s.id, e.target.value)}
                              placeholder="https://…"
                              className="flex-1"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="shrink-0 text-muted-foreground hover:text-destructive"
                              onClick={() => removeSocialLink(s.id)}
                              title={
                                DEFAULT_SOCIAL_IDS.includes(
                                  s.id as (typeof DEFAULT_SOCIAL_IDS)[number]
                                )
                                  ? 'Clear URL'
                                  : 'Remove'
                              }
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addSocialLink}
                          className="gap-1"
                        >
                          <Plus className="w-3.5 h-3.5" /> Add link
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Homepage programs & ecosystem snapshot */}
            {activeTab === 'home' && (
            <div className="pt-4 border-t border-border space-y-6">
              <div>
                <h3 className="text-sm font-semibold mb-2">Homepage: signature programs</h3>
                <p className="text-xs text-muted-foreground mb-3">
                  These cards appear in the &quot;Signature tracks&quot; section of the homepage.
                  Keep them short and focused.
                </p>
                <div className="space-y-3">
                  {featuredPrograms.map((p, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg border border-border/70 p-3 space-y-2 bg-background/40"
                    >
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">
                          Program {idx + 1}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() =>
                            setFeaturedPrograms((prev) => prev.filter((_, i) => i !== idx))
                          }
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <Input
                        value={p.title ?? ''}
                        onChange={(e) =>
                          setFeaturedPrograms((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, title: e.target.value } : row,
                            ),
                          )
                        }
                        placeholder="Program title (e.g. Founders Lab)"
                        className="text-sm"
                      />
                      <Input
                        value={p.stage ?? ''}
                        onChange={(e) =>
                          setFeaturedPrograms((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, stage: e.target.value } : row,
                            ),
                          )
                        }
                        placeholder="Stage (e.g. Idea · Pre-seed)"
                        className="text-xs"
                      />
                      <Input
                        value={p.slug ?? ''}
                        onChange={(e) =>
                          setFeaturedPrograms((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, slug: e.target.value } : row,
                            ),
                          )
                        }
                        placeholder="Optional program slug (for deep-linking)"
                        className="text-xs"
                      />
                      <textarea
                        value={p.summary ?? ''}
                        onChange={(e) =>
                          setFeaturedPrograms((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, summary: e.target.value } : row,
                            ),
                          )
                        }
                        placeholder="Short summary"
                        rows={3}
                        className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                      />
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1 text-xs"
                    onClick={() =>
                      setFeaturedPrograms((prev) => [
                        ...prev,
                        { title: '', stage: '', slug: '', summary: '' },
                      ])
                    }
                  >
                    <Plus className="w-3 h-3" /> Add program
                  </Button>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-2">Homepage: ecosystem stats</h3>
                <p className="text-xs text-muted-foreground mb-3">
                  These small stat cards appear above the innovation pillars (e.g. startups
                  supported, capital catalysed).
                </p>
                <div className="space-y-3">
                  {ecosystemStats.map((s, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg border border-border/70 p-3 space-y-2 bg-background/40"
                    >
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">
                          Stat {idx + 1}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() =>
                            setEcosystemStats((prev) => prev.filter((_, i) => i !== idx))
                          }
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <Input
                        value={s.value ?? ''}
                        onChange={(e) =>
                          setEcosystemStats((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, value: e.target.value } : row,
                            ),
                          )
                        }
                        placeholder="Value (e.g. 120+)"
                        className="text-sm"
                      />
                      <Input
                        value={s.label ?? ''}
                        onChange={(e) =>
                          setEcosystemStats((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, label: e.target.value } : row,
                            ),
                          )
                        }
                        placeholder="Label (e.g. Startups supported)"
                        className="text-xs"
                      />
                      <label className="flex items-center gap-2 text-[11px] text-muted-foreground mt-1">
                        <input
                          type="checkbox"
                          checked={Boolean(s.highlight)}
                          onChange={(e) =>
                            setEcosystemStats((prev) =>
                              prev.map((row, i) =>
                                i === idx ? { ...row, highlight: e.target.checked } : row,
                              ),
                            )
                          }
                        />
                        Highlight this stat
                      </label>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1 text-xs"
                    onClick={() =>
                      setEcosystemStats((prev) => [
                        ...prev,
                        { label: '', value: '', highlight: false },
                      ])
                    }
                  >
                    <Plus className="w-3 h-3" /> Add stat
                  </Button>
                </div>
              </div>
            </div>
            )}

            {/* About page content */}
            {activeTab === 'about' && (
            <div className="pt-4 border-t border-border space-y-6">
              <h3 className="text-sm font-semibold">About page content</h3>
              <p className="text-xs text-muted-foreground">
                Manage hero text/images and section cards shown on the public About page.
              </p>

              <div className="space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Hero</h4>
                <div>
                  <Label className="mb-1 text-xs">Heading</Label>
                  <Input value={aboutHeroHeading} onChange={(e) => setAboutHeroHeading(e.target.value)} />
                </div>
                <div>
                  <Label className="mb-1 text-xs">Subtext</Label>
                  <textarea value={aboutHeroSubtext} onChange={(e) => setAboutHeroSubtext(e.target.value)} rows={3} className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs" />
                </div>
                <div>
                  <Label className="mb-1 text-xs">Expanded paragraph</Label>
                  <textarea value={aboutHeroParagraph} onChange={(e) => setAboutHeroParagraph(e.target.value)} rows={3} className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="mb-1 text-xs">Mission image</Label>
                    <div className="flex gap-2">
                      <Input value={aboutMissionImage} onChange={(e) => setAboutMissionImage(e.target.value)} className="text-xs" placeholder="Uploaded image URL appears here" />
                      <Button type="button" variant="outline" size="sm" onClick={() => void pickAndUploadImage((url) => setAboutMissionImage(url))}>Upload</Button>
                    </div>
                  </div>
                  <div>
                    <Label className="mb-1 text-xs">Build section image</Label>
                    <div className="flex gap-2">
                      <Input value={aboutBuildImage} onChange={(e) => setAboutBuildImage(e.target.value)} className="text-xs" placeholder="Uploaded image URL appears here" />
                      <Button type="button" variant="outline" size="sm" onClick={() => void pickAndUploadImage((url) => setAboutBuildImage(url))}>Upload</Button>
                    </div>
                  </div>
                  <div>
                    <Label className="mb-1 text-xs">Who section image</Label>
                    <div className="flex gap-2">
                      <Input value={aboutWhoImage} onChange={(e) => setAboutWhoImage(e.target.value)} className="text-xs" placeholder="Uploaded image URL appears here" />
                      <Button type="button" variant="outline" size="sm" onClick={() => void pickAndUploadImage((url) => setAboutWhoImage(url))}>Upload</Button>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Hero carousel images (6+ recommended)</Label>
                    <Button type="button" variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setAboutHeroImages((prev) => [...prev, ''])}>
                      <Plus className="w-3 h-3" /> Add image
                    </Button>
                  </div>
                  {aboutHeroImages.map((img, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input value={img ?? ''} onChange={(e) => setAboutHeroImages((prev) => prev.map((x, i) => (i === idx ? e.target.value : x)))} placeholder="https://..." className="text-xs" />
                      <Button type="button" variant="outline" size="sm" onClick={() => void pickAndUploadImage((url) => setAboutHeroImages((prev) => prev.map((x, i) => (i === idx ? url : x))))}>Upload</Button>
                      <Button type="button" variant="ghost" size="icon" onClick={() => setAboutHeroImages((prev) => prev.filter((_, i) => i !== idx))}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="space-y-2 pt-2 border-t border-border/60">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Hero logo carousel (below hero)</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1 text-xs"
                      onClick={() =>
                        setAboutLogoCarousel((prev) => ({
                          ...prev,
                          items: [...prev.items, { url: '', name: '' }],
                        }))
                      }
                    >
                      <Plus className="w-3 h-3" /> Add logo
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Optional heading plus partner logos. Shown as a scrolling strip at the bottom of the About hero.
                  </p>
                  <div>
                    <Label className="mb-1 text-xs">Carousel heading (optional)</Label>
                    <Input
                      value={aboutLogoCarousel.title}
                      onChange={(e) =>
                        setAboutLogoCarousel((prev) => ({ ...prev, title: e.target.value }))
                      }
                      placeholder="e.g. Trusted by partners"
                      className="text-xs"
                    />
                  </div>
                  {aboutLogoCarousel.items.map((it, idx) => (
                    <div key={idx} className="rounded-lg border border-border/70 p-3 space-y-2 bg-background/40">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">Logo {idx + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            setAboutLogoCarousel((prev) => ({
                              ...prev,
                              items: prev.items.filter((_, i) => i !== idx),
                            }))
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <Input
                        value={it.name}
                        onChange={(e) =>
                          setAboutLogoCarousel((prev) => ({
                            ...prev,
                            items: prev.items.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x)),
                          }))
                        }
                        placeholder="Name / alt text (optional)"
                        className="text-xs"
                      />
                      <div className="flex gap-2">
                        <Input
                          value={it.url}
                          onChange={(e) =>
                            setAboutLogoCarousel((prev) => ({
                              ...prev,
                              items: prev.items.map((x, i) => (i === idx ? { ...x, url: e.target.value } : x)),
                            }))
                          }
                          placeholder="Logo image URL"
                          className="text-xs"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            void pickAndUploadImage((url) =>
                              setAboutLogoCarousel((prev) => ({
                                ...prev,
                                items: prev.items.map((x, i) => (i === idx ? { ...x, url } : x)),
                              }))
                            )
                          }
                        >
                          Upload
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="space-y-2 pt-2 border-t border-border/60">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Africa to the world: city carousel images</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1 text-xs"
                      onClick={() =>
                        setAboutEastAfricaCityImages((prev) => [
                          ...prev,
                          { city: '', image: '' },
                        ])
                      }
                    >
                      <Plus className="w-3 h-3" /> Add city image
                    </Button>
                  </div>
                  {aboutEastAfricaCityImages.map((item, idx) => (
                    <div key={idx} className="rounded-lg border border-border/70 p-3 space-y-2 bg-background/40">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">Slide {idx + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            setAboutEastAfricaCityImages((prev) =>
                              prev.filter((_, i) => i !== idx),
                            )
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <Input
                        value={item.city ?? ''}
                        onChange={(e) =>
                          setAboutEastAfricaCityImages((prev) =>
                            prev.map((x, i) =>
                              i === idx ? { ...x, city: e.target.value } : x,
                            ),
                          )
                        }
                        placeholder="City label (e.g. Nairobi, Kenya)"
                        className="text-xs"
                      />
                      <div className="flex gap-2">
                        <Input
                          value={item.image ?? ''}
                          onChange={(e) =>
                            setAboutEastAfricaCityImages((prev) =>
                              prev.map((x, i) =>
                                i === idx ? { ...x, image: e.target.value } : x,
                              ),
                            )
                          }
                          placeholder="City image URL"
                          className="text-xs"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            void pickAndUploadImage((url) =>
                              setAboutEastAfricaCityImages((prev) =>
                                prev.map((x, i) =>
                                  i === idx ? { ...x, image: url } : x,
                                ),
                              ),
                            )
                          }
                        >
                          Upload
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Mission and vision</h4>
                <div>
                  <Label className="mb-1 text-xs">Mission</Label>
                  <textarea value={aboutMission} onChange={(e) => setAboutMission(e.target.value)} rows={3} className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs" />
                </div>
                <div>
                  <Label className="mb-1 text-xs">Vision</Label>
                  <textarea value={aboutVision} onChange={(e) => setAboutVision(e.target.value)} rows={3} className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs" />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">How Homeland creates value (cards)</Label>
                  <Button type="button" variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setAboutValuePillars((prev) => [...prev, { title: '', body: '', image: '' }])}>
                    <Plus className="w-3 h-3" /> Add card
                  </Button>
                </div>
                {aboutValuePillars.map((p, idx) => (
                  <div key={idx} className="rounded-lg border border-border/70 p-3 space-y-2 bg-background/40">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">Card {idx + 1}</span>
                      <Button type="button" variant="ghost" size="icon" onClick={() => setAboutValuePillars((prev) => prev.filter((_, i) => i !== idx))}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                    <Input value={p?.title ?? ''} onChange={(e) => setAboutValuePillars((prev) => prev.map((x, i) => (i === idx ? { ...x, title: e.target.value } : x)))} placeholder="Title" className="text-xs" />
                    <div className="flex gap-2">
                      <Input value={p?.image ?? ''} onChange={(e) => setAboutValuePillars((prev) => prev.map((x, i) => (i === idx ? { ...x, image: e.target.value } : x)))} placeholder="Image URL" className="text-xs" />
                      <Button type="button" variant="outline" size="sm" onClick={() => void pickAndUploadImage((url) => setAboutValuePillars((prev) => prev.map((x, i) => (i === idx ? { ...x, image: url } : x))))}>Upload</Button>
                    </div>
                    <textarea value={p?.body ?? ''} onChange={(e) => setAboutValuePillars((prev) => prev.map((x, i) => (i === idx ? { ...x, body: e.target.value } : x)))} rows={3} className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs" placeholder="Body" />
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Core values</Label>
                  <Button type="button" variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setAboutCoreValues((prev) => [...prev, { title: '', body: '' }])}>
                    <Plus className="w-3 h-3" /> Add value
                  </Button>
                </div>
                {aboutCoreValues.map((v, idx) => (
                  <div key={idx} className="rounded-lg border border-border/70 p-3 space-y-2 bg-background/40">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">Value {idx + 1}</span>
                      <Button type="button" variant="ghost" size="icon" onClick={() => setAboutCoreValues((prev) => prev.filter((_, i) => i !== idx))}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                    <Input value={v?.title ?? ''} onChange={(e) => setAboutCoreValues((prev) => prev.map((x, i) => (i === idx ? { ...x, title: e.target.value } : x)))} placeholder="Title" className="text-xs" />
                    <textarea value={v?.body ?? ''} onChange={(e) => setAboutCoreValues((prev) => prev.map((x, i) => (i === idx ? { ...x, body: e.target.value } : x)))} rows={2} className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs" placeholder="Description" />
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Build areas</Label>
                    <Button type="button" variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setAboutBuildAreas((prev) => [...prev, ''])}><Plus className="w-3 h-3" /> Add</Button>
                  </div>
                  {aboutBuildAreas.map((x, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input value={x ?? ''} onChange={(e) => setAboutBuildAreas((prev) => prev.map((v, i) => (i === idx ? e.target.value : v)))} className="text-xs" />
                      <Button type="button" variant="ghost" size="icon" onClick={() => setAboutBuildAreas((prev) => prev.filter((_, i) => i !== idx))}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Who it is for</Label>
                    <Button type="button" variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setAboutWhoFor((prev) => [...prev, ''])}><Plus className="w-3 h-3" /> Add</Button>
                  </div>
                  {aboutWhoFor.map((x, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input value={x ?? ''} onChange={(e) => setAboutWhoFor((prev) => prev.map((v, i) => (i === idx ? e.target.value : v)))} className="text-xs" />
                      <Button type="button" variant="ghost" size="icon" onClick={() => setAboutWhoFor((prev) => prev.filter((_, i) => i !== idx))}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Traction stats</Label>
                  <Button type="button" variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setAboutTractionStats((prev) => [...prev, { value: '', label: '' }])}>
                    <Plus className="w-3 h-3" /> Add stat
                  </Button>
                </div>
                {aboutTractionStats.map((s, idx) => (
                  <div key={idx} className="rounded-lg border border-border/70 p-3 space-y-2 bg-background/40">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">Stat {idx + 1}</span>
                      <Button type="button" variant="ghost" size="icon" onClick={() => setAboutTractionStats((prev) => prev.filter((_, i) => i !== idx))}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                    <Input value={s?.value ?? ''} onChange={(e) => setAboutTractionStats((prev) => prev.map((x, i) => (i === idx ? { ...x, value: e.target.value } : x)))} placeholder="Value (e.g. 25+)" className="text-xs" />
                    <Input value={s?.label ?? ''} onChange={(e) => setAboutTractionStats((prev) => prev.map((x, i) => (i === idx ? { ...x, label: e.target.value } : x)))} placeholder="Label" className="text-xs" />
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* Ecosystem page content */}
            {activeTab === 'ecosystem' && (
            <div className="pt-4 border-t border-border space-y-4">
              <h3 className="text-sm font-semibold">Ecosystem page: hubs, partners, events</h3>
              <p className="text-xs text-muted-foreground">
                Map out the key pieces of your ecosystem. These lists power the cards on the
                Ecosystem page.
              </p>

              {/* Hubs */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Hubs &amp; communities</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1 text-xs"
                    onClick={() =>
                      setEcosystemHubs((prev) => [
                        ...prev,
                        { name: '', region: '', location: '', summary: '', image: '' },
                      ])
                    }
                  >
                    <Plus className="w-3 h-3" /> Add hub
                  </Button>
                </div>
                <div className="space-y-2">
                  {ecosystemHubs.map((h, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg border border-border/70 p-3 space-y-2 bg-background/40"
                    >
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">
                          Hub {idx + 1}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() =>
                            setEcosystemHubs((prev) => prev.filter((_, i) => i !== idx))
                          }
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <Input
                        value={h.name ?? ''}
                        onChange={(e) =>
                          setEcosystemHubs((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, name: e.target.value } : row,
                            ),
                          )
                        }
                        placeholder="Hub name"
                        className="text-sm"
                      />
                      <Input
                        value={h.region ?? ''}
                        onChange={(e) =>
                          setEcosystemHubs((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, region: e.target.value } : row,
                            ),
                          )
                        }
                        placeholder="Region (e.g. East Africa, Remote)"
                        className="text-xs"
                      />
                      <Input
                        value={h.location ?? ''}
                        onChange={(e) =>
                          setEcosystemHubs((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, location: e.target.value } : row,
                            ),
                          )
                        }
                        placeholder="Location (city / online)"
                        className="text-xs"
                      />
                      <textarea
                        value={h.summary ?? ''}
                        onChange={(e) =>
                          setEcosystemHubs((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, summary: e.target.value } : row,
                            ),
                          )
                        }
                        rows={2}
                        className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                        placeholder="Short description"
                      />
                      <div className="flex gap-2">
                        <Input
                          value={h.image ?? ''}
                          onChange={(e) =>
                            setEcosystemHubs((prev) =>
                              prev.map((row, i) => (i === idx ? { ...row, image: e.target.value } : row)),
                            )
                          }
                          placeholder="Image URL"
                          className="text-xs"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            void pickAndUploadImage((url) =>
                              setEcosystemHubs((prev) =>
                                prev.map((row, i) => (i === idx ? { ...row, image: url } : row)),
                              ),
                            )
                          }
                        >
                          Upload
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Partners */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Partners</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1 text-xs"
                    onClick={() =>
                      setEcosystemPartners((prev) => [
                        ...prev,
                        { name: '', type: '', summary: '', link: '', image: '' },
                      ])
                    }
                  >
                    <Plus className="w-3 h-3" /> Add partner
                  </Button>
                </div>
                <div className="space-y-2">
                  {ecosystemPartners.map((p, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg border border-border/70 p-3 space-y-2 bg-background/40"
                    >
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">
                          Partner {idx + 1}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() =>
                            setEcosystemPartners((prev) => prev.filter((_, i) => i !== idx))
                          }
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <Input
                        value={p.name ?? ''}
                        onChange={(e) =>
                          setEcosystemPartners((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, name: e.target.value } : row,
                            ),
                          )
                        }
                        placeholder="Partner name"
                        className="text-sm"
                      />
                      <Input
                        value={p.type ?? ''}
                        onChange={(e) =>
                          setEcosystemPartners((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, type: e.target.value } : row,
                            ),
                          )
                        }
                        placeholder="Type (e.g. Investor, University)"
                        className="text-xs"
                      />
                      <Input
                        value={p.link ?? ''}
                        onChange={(e) =>
                          setEcosystemPartners((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, link: e.target.value } : row,
                            ),
                          )
                        }
                        placeholder="Optional external link"
                        className="text-xs"
                      />
                      <textarea
                        value={p.summary ?? ''}
                        onChange={(e) =>
                          setEcosystemPartners((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, summary: e.target.value } : row,
                            ),
                          )
                        }
                        rows={2}
                        className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                        placeholder="Short description"
                      />
                      <div className="flex gap-2">
                        <Input
                          value={p.image ?? ''}
                          onChange={(e) =>
                            setEcosystemPartners((prev) =>
                              prev.map((row, i) => (i === idx ? { ...row, image: e.target.value } : row)),
                            )
                          }
                          placeholder="Image URL"
                          className="text-xs"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            void pickAndUploadImage((url) =>
                              setEcosystemPartners((prev) =>
                                prev.map((row, i) => (i === idx ? { ...row, image: url } : row)),
                              ),
                            )
                          }
                        >
                          Upload
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Events */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Events &amp; rituals</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1 text-xs"
                    onClick={() =>
                      setEcosystemEvents((prev) => [
                        ...prev,
                        { name: '', cadence: '', summary: '', image: '' },
                      ])
                    }
                  >
                    <Plus className="w-3 h-3" /> Add event
                  </Button>
                </div>
                <div className="space-y-2">
                  {ecosystemEvents.map((e, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg border border-border/70 p-3 space-y-2 bg-background/40"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-muted-foreground">
                          Event {idx + 1}
                        </span>
                      </div>
                      <Input
                        value={e.name ?? ''}
                        onChange={(ev) =>
                          setEcosystemEvents((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, name: ev.target.value } : row,
                            ),
                          )
                        }
                        placeholder="Event name (e.g. Founder Showcase)"
                        className="text-sm"
                      />
                      <Input
                        value={e.cadence ?? ''}
                        onChange={(ev) =>
                          setEcosystemEvents((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, cadence: ev.target.value } : row,
                            ),
                          )
                        }
                        placeholder="Cadence (e.g. Twice a year, Monthly)"
                        className="text-xs"
                      />
                      <textarea
                        value={e.summary ?? ''}
                        onChange={(ev) =>
                          setEcosystemEvents((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, summary: ev.target.value } : row,
                            ),
                          )
                        }
                        rows={2}
                        className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                        placeholder="Short description"
                      />
                      <div className="flex gap-2">
                        <Input
                          value={e.image ?? ''}
                          onChange={(ev) =>
                            setEcosystemEvents((prev) =>
                              prev.map((row, i) => (i === idx ? { ...row, image: ev.target.value } : row)),
                            )
                          }
                          placeholder="Image URL"
                          className="text-xs"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            void pickAndUploadImage((url) =>
                              setEcosystemEvents((prev) =>
                                prev.map((row, i) => (i === idx ? { ...row, image: url } : row)),
                              ),
                            )
                          }
                        >
                          Upload
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-accent text-accent-foreground hover:bg-gold-light"
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>
    </SlideOver>
  );
}
